import { useState, useEffect } from "react";
import { Sparkles, Shield, Target, TrendingUp, BookOpen, Loader2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAITutor, type CheckpointDebriefData } from "@/hooks/useAITutor";
import { cn } from "@/lib/utils";

const FALLBACK: CheckpointDebriefData = {
  learner_summary: "You've completed this checkpoint. Some areas are strong, others need more focus.",
  parent_summary: "Your child completed a checkpoint assessment. Review the breakdown below for details.",
  admin_insight: "Standard assessment completion with mixed results.",
  strengths: ["Showed persistence", "Attempted all questions"],
  weak_spots: ["Some core concepts need reinforcement"],
  skill_breakdown: [{ skill: "Overall", rating: "developing", note: "More practice needed" }],
  recommendation_path: "Review related lessons and retry when ready.",
  confidence_note: "Building confidence takes repetition — keep going.",
};

interface Props {
  assessmentTitle: string;
  assessmentType: string;
  score: number;
  maxScore: number;
  passed: boolean;
  skillBreakdown?: Record<string, unknown>;
  strengths?: string[];
  weaknesses?: string[];
  ageBracket?: string;
  viewMode?: "learner" | "parent" | "admin";
}

const RATING_STYLES = {
  strong: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "Strong" },
  developing: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "Developing" },
  weak: { bg: "bg-destructive/10", border: "border-destructive/20", text: "text-destructive", label: "Needs Work" },
};

export default function CheckpointDebriefPanel({
  assessmentTitle, assessmentType, score, maxScore, passed,
  skillBreakdown, strengths, weaknesses, ageBracket, viewMode = "learner",
}: Props) {
  const { loading, getCheckpointDebrief } = useAITutor();
  const [data, setData] = useState<CheckpointDebriefData | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      const result = await getCheckpointDebrief({
        assessmentTitle, assessmentType, score, maxScore, passed,
        skillBreakdown, strengths, weaknesses, ageBracket,
      });
      if (cancelled) return;
      if (result.fallback || !result.data) setUseFallback(true);
      else setData(result.data);
    };
    fetch();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  const content = data || (useFallback ? FALLBACK : null);

  const summaryText = content
    ? viewMode === "parent" ? content.parent_summary
    : viewMode === "admin" ? content.admin_insight
    : content.learner_summary
    : "";

  return (
    <div className="rounded-2xl border border-primary/15 bg-card/90 backdrop-blur-md p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Shield className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-sm text-foreground">AI Performance Debrief</h3>
          <p className="text-[10px] text-muted-foreground">{assessmentTitle}</p>
        </div>
      </div>

      {loading && !content ? (
        <div className="flex items-center justify-center py-6 gap-2">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Generating debrief…</span>
        </div>
      ) : content ? (
        <>
          <p className="text-sm text-muted-foreground leading-relaxed">{summaryText}</p>

          {/* Strengths & Weak spots */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-400 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Strengths
              </p>
              {content.strengths.map((s, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-emerald-400 mt-0.5">•</span> {s}
                </p>
              ))}
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400 flex items-center gap-1">
                <Target className="w-3 h-3" /> Focus Areas
              </p>
              {content.weak_spots.map((w, i) => (
                <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-amber-400 mt-0.5">•</span> {w}
                </p>
              ))}
            </div>
          </div>

          {/* Skill Breakdown (expandable) */}
          {content.skill_breakdown.length > 0 && (
            <div>
              <button
                onClick={() => setExpanded(v => !v)}
                className="flex items-center gap-1.5 text-xs font-heading font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", expanded && "rotate-180")} />
                Skill Breakdown ({content.skill_breakdown.length})
              </button>
              {expanded && (
                <div className="mt-2 space-y-1.5">
                  {content.skill_breakdown.map((sb, i) => {
                    const style = RATING_STYLES[sb.rating] || RATING_STYLES.developing;
                    return (
                      <div key={i} className={cn("flex items-center gap-2 rounded-lg border p-2", style.bg, style.border)}>
                        <span className={cn("text-[10px] font-heading font-bold uppercase", style.text)}>{style.label}</span>
                        <span className="text-xs text-foreground font-heading flex-1">{sb.skill}</span>
                        <span className="text-[10px] text-muted-foreground">{sb.note}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Recommendation */}
          <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 space-y-1">
            <div className="flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Recommended Path</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{content.recommendation_path}</p>
          </div>

          {content.confidence_note && (
            <p className="text-center text-xs text-muted-foreground/70 italic">{content.confidence_note}</p>
          )}
        </>
      ) : null}
    </div>
  );
}
